import { useEffect, useRef } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BRAND } from "../data/landing";
import { Magnetic } from "../components/Magnetic";
import HeroCursorTrail from "./HeroCursorTrail";
import { trackCTA } from "../lib/analytics";

const HERO_EASE = [0.22, 1, 0.36, 1];

const HERO_BG_STYLE = {
  filter: "brightness(1.35) contrast(1.08) saturate(1.12)",
  imageRendering: "-webkit-optimize-contrast",
};

const HERO_ACTIONS = [
  { href: "#get-started", label: "Start Growing Today", icon: ArrowRight, primary: true, testId: "hero-start-growing-btn" },
  { href: "#logos", label: "View Our Work", icon: ArrowRight, primary: false, testId: "hero-view-work-btn" },
  { href: "#about", label: "Meet Our Team", icon: ArrowRight, primary: false, testId: "hero-meet-team-btn" },
  { href: BRAND.bookMeetingUrl, label: "Book A Meeting", icon: ArrowUpRight, primary: true, external: true, testId: "hero-book-meeting-btn" },
];

// Cursor-following spotlight — sets CSS vars --mx / --my on the section
function useHeroSpotlight(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    let nextX = 50;
    let nextY = 35;

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      nextX = ((e.clientX - r.left) / r.width) * 100;
      nextY = ((e.clientY - r.top) / r.height) * 100;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--mx", `${nextX}%`);
        el.style.setProperty("--my", `${nextY}%`);
        raf = 0;
      });
    };

    const onLeave = () => {
      el.style.setProperty("--mx", `50%`);
      el.style.setProperty("--my", `35%`);
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref is stable; remaining identifiers are globals/effect-locals, not reactive
  }, [ref]);
}

function HeroBackground() {
  if (BRAND.heroVideo) {
    return (
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src={BRAND.heroVideo}
        poster={BRAND.heroBg}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        style={HERO_BG_STYLE}
        aria-hidden="true"
      />
    );
  }
  return (
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ ...HERO_BG_STYLE, backgroundImage: `url(${BRAND.heroBg})`, backgroundSize: "cover" }}
      aria-hidden="true"
    />
  );
}

function HeroActions() {
  return (
    <motion.div
      className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full max-w-2xl"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.35, ease: HERO_EASE }}
    >
      {HERO_ACTIONS.map(({ href, label, icon: Icon, primary, external, testId }) => {
        const btn = (
          <a
            href={href}
            {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            onClick={() => trackCTA(label, "hero")}
            className={`btn-kmg inline-flex w-full items-center justify-between gap-3 px-7 py-4 font-bold tracking-wide text-white transition-transform duration-300 hover:-translate-y-0.5 ${
              primary
                ? "bg-[#1440E1] hover:bg-[#1033B3]"
                : "border border-white/25 bg-white/10 hover:bg-white/20"
            }`}
            data-testid={testId}
          >
            <span>{label}</span>
            <Icon size={18} />
          </a>
        );
        return primary ? (
          <Magnetic key={label} strength={0.35}>
            {btn}
          </Magnetic>
        ) : (
          <div key={label}>{btn}</div>
        );
      })}
    </motion.div>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  useHeroSpotlight(sectionRef);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.18]);

  return (
    <section
      ref={sectionRef}
      id="top"
      className="hero-spotlight relative isolate min-h-[88vh] flex items-center overflow-hidden"
      data-testid="hero-section"
      style={{ "--mx": "50%", "--my": "35%" }}
    >
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={{ y: bgY, scale: bgScale }}
        aria-hidden="true"
      >
        <HeroBackground />
      </motion.div>

      <HeroCursorTrail />

      {/* Cinematic fade-in — dark veil clears on load */}
      <motion.div
        className="absolute inset-0 z-40 bg-[#050B26] pointer-events-none"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 1.1, ease: "easeOut" }}
        aria-hidden="true"
      />

      {/* Pulsing brand glow */}
      <div className="hero-glow-pulse" aria-hidden="true" />

      {/* Animated gradient mesh — soft brand-blue blobs drifting */}
      <div className="hero-mesh" aria-hidden="true">
        <span />
      </div>
      <div className="absolute inset-0 hero-overlay-center" aria-hidden="true" />

      {/* Cursor-following spotlight — sits above overlay, below grain */}
      <div className="hero-cursor-spotlight" aria-hidden="true" />

      <div className="absolute inset-0 hero-grain-strong" aria-hidden="true" />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
          <motion.h1
            className="font-arial-black text-white leading-[1.02] tracking-tight"
            style={{ fontSize: "clamp(46px, 6.5vw, 84px)" }}
            data-testid="hero-headline"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease: HERO_EASE }}
          >
            <span className="block sm:whitespace-nowrap">
              Strategy. <span className="text-[#1440E1]">Innovation.</span>
            </span>
            <span className="block">Growth.</span>
          </motion.h1>

          <motion.p
            className="mt-6 text-white/95 max-w-3xl leading-relaxed"
            style={{ fontSize: "clamp(20px, 1.5vw, 24px)" }}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: HERO_EASE }}
          >
            Knight Media Group helps brands grow through strategic marketing,
            creative storytelling, and modern digital solutions. We build
            stronger online presence, connect businesses with the right
            audience, and turn digital attention into measurable growth. From
            content planning and social media management to paid advertising,
            community engagement, and performance analytics, we create digital
            solutions designed to strengthen your online presence and support
            real business growth.
          </motion.p>

          <HeroActions />
        </div>
      </div>

      {/* Animated scroll-down indicator */}
      <motion.a
        href="#services"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 hidden sm:flex flex-col items-center gap-2 text-white/60 hover:text-white transition-colors"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        aria-label="Scroll to services"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] font-semibold">
          Scroll
        </span>
        <span className="relative flex h-9 w-5 items-start justify-center rounded-full border border-white/40 p-1">
          <motion.span
            className="h-1.5 w-1 rounded-full bg-[#5b80ff]"
            animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.a>
    </section>
  );
}
